import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { CheckCircle2, Clock } from 'lucide-react';
import type { InvitedBy } from '@clareefai/types';

/**
 * Binary first-login indicator: green "Logged in" once the user has authenticated
 * at least once, amber "Pending first login" otherwise.
 */
export function FirstLoginChip({
  loggedIn,
  className = '',
}: {
  loggedIn?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const cls = loggedIn
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : 'bg-amber-100 text-amber-800 border-amber-300';
  const Icon = loggedIn ? CheckCircle2 : Clock;
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border ${cls} ${className}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {loggedIn ? t('common.firstLogin.loggedIn') : t('common.firstLogin.pending')}
    </span>
  );
}

/**
 * "Invited by {name}" line — shows the referring promoter, or the team member /
 * vendor who created the record. Renders nothing when no inviter is known.
 */
export function InvitedByLine({
  invitedBy,
  className = '',
}: {
  invitedBy?: InvitedBy | null;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!invitedBy?.name) return null;

  const typeLabel = t(`common.invitedByType.${invitedBy.type}`, { defaultValue: '' });

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Avatar
        src={
          typeof invitedBy.avatar === 'string' && invitedBy.avatar.length > 2
            ? invitedBy.avatar
            : undefined
        }
        name={invitedBy.name}
        size="sm"
      />
      <div className="leading-tight">
        <div className="text-sm font-medium">{invitedBy.name}</div>
        <div className="text-xs opacity-70">
          {t('common.invitedBy')}
          {typeLabel ? ` · ${typeLabel}` : ''}
        </div>
      </div>
    </div>
  );
}
